<template>
    <section class="text-foxy-text" :aria-busy="previewReloadPending ? 'true' : 'false'">
        <div v-if="!hideHeader" class="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 class="text-xl font-bold text-foxy-text">
                Цитати <span class="text-foxy-text-secondary">· {{ localTotalCount }}</span>
            </h3>
            <div class="flex flex-wrap items-center gap-3">
                <button v-if="allowCreate && isAuthenticated" @click="openQuoteModal"
                    class="foxy-btn-primary !px-4 !py-2 !font-semibold">
                    <i class="fas fa-plus" aria-hidden="true"></i>
                    Додати цитату
                </button>
                <a v-if="allUrl && localTotalCount > 4" :href="allUrl"
                    class="inline-flex items-center gap-2 rounded-lg text-sm font-semibold text-foxy-action transition-colors hover:text-foxy-action/80 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20">
                    Переглянути всі
                    <i class="fas fa-arrow-right text-xs" aria-hidden="true"></i>
                </a>
            </div>
        </div>
        <p v-if="previewReloadError" role="status" class="mb-4 text-sm text-foxy-text-secondary">
            Не вдалося оновити список.
            <button type="button" class="font-semibold text-foxy-action hover:underline"
                :disabled="previewReloadPending" @click="refreshPreview">Спробувати ще раз</button>
        </p>
        <div class="flex flex-col justify-end mb-4" v-if="localQuotes.length > 0" >
            <div class="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
                <quote-card v-for="quote in localQuotes" :key="quote.id" :quote="quote" :book-slug="quote.book?.slug || bookSlug" :presentation-mode="presentationMode" :show-book-context="showBookContext"
                    :current-user-id="currentUserId" @like-toggled="handleLikeToggled" @quote-deleted="handleQuoteDeleted"
                    @quote-updated="handleQuoteUpdated" @show-notification="showNotification" />
            </div>
        </div>
        <div v-else class="text-center py-20">
            <div
                class="w-32 h-32 mx-auto mb-8 bg-foxy-elevated rounded-full flex items-center justify-center">
                <svg class="w-16 h-16 text-foxy-text-secondary" fill="currentColor" viewBox="0 0 24 24">
                    <path
                        d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
            </div>
            <h3 class="text-3xl font-black text-foxy-text mb-4">Поки немає цитат</h3>
            <p class="text-foxy-text-secondary text-xl font-medium">Станьте першим, хто додасть улюблену цитату
                з книги</p>
        </div>
    </section>
</template>

<script>
import QuoteCard from './QuoteCard.vue';
import axios from 'axios';

export default {
    name: 'QuotesList',
    components: {
        QuoteCard
    },
    props: {
        quotes: {
            type: [Array, Object],
            default: () => []
        },
        bookSlug: {
            type: String,
            default: ''
        },
        currentUserId: {
            type: Number,
            default: null
        },
        hideHeader: {
            type: Boolean,
            default: false
        },
        presentationMode: { type: String, default: 'compact' },
        totalCount: { type: Number, default: null },
        allUrl: { type: String, default: '' },
        allowCreate: { type: Boolean, default: true },
        showBookContext: { type: Boolean, default: false }
    },
    data() {
        return {
            localQuotes: Array.isArray(this.quotes) ? this.quotes.slice(0, 4) : Object.values(this.quotes || {}).slice(0, 4),
            previewReloadPending: false,
            previewReloadError: false,
            previewReloadVersion: 0,
            localTotalCount: this.totalCount ?? Object.values(this.quotes || {}).length,
        };
    },
    computed: {
        totalQuotesCount() {
            return this.localTotalCount;
        },
        isAuthenticated() {
            return this.currentUserId !== null;
        }
    },
    mounted() {
        window.addEventListener('quote-added', this.onQuoteAdded);
    },
    beforeDestroy() {
        window.removeEventListener('quote-added', this.onQuoteAdded);
        this.invalidatePreviewReload();
    },
    watch: {
        totalCount(value) { this.localTotalCount = Math.max(0, value ?? this.localQuotes.length); }
    },
    methods: {
        invalidatePreviewReload() {
            this.previewReloadVersion += 1;
            this.previewReloadPending = false;
            this.previewReloadError = false;
        },
        refillPreviewIfNeeded() {
            if (this.localQuotes.length < Math.min(4, this.localTotalCount)) return this.refreshPreview();
        },
        async refreshPreview() {
            const version = ++this.previewReloadVersion;
            this.previewReloadPending = true;
            this.previewReloadError = false;
            try {
                // Reload the existing Show payload: archive APIs have different visibility contracts.
                const response = await axios.get(window.location.pathname + window.location.search, {
                    headers: { Accept: 'text/html' },
                });
                if (version !== this.previewReloadVersion) return;
                if (typeof response.data !== 'string') throw new Error('Invalid preview response');
                const page = new window.DOMParser().parseFromString(response.data, 'text/html');
                const lists = [...page.querySelectorAll('quotes-list')]
                    .filter(node => node.getAttribute('book-slug') === this.bookSlug);
                if (lists.length !== 1) throw new Error('Preview affiliation unavailable');
                const rows = JSON.parse(lists[0].getAttribute(':quotes'));
                const countValue = lists[0].getAttribute(':total-count');
                const total = Number(countValue);
                if (!countValue || !Number.isSafeInteger(total) || total < 0 || !Array.isArray(rows)
                    || rows.length !== Math.min(4, total)
                    || rows.some(row => !row || !Number.isSafeInteger(row.id) || typeof row.content !== 'string')
                    || new Set(rows.map(row => row.id)).size !== rows.length) throw new Error('Invalid preview payload');
                this.localQuotes = rows;
                this.localTotalCount = total;
            } catch (error) {
                if (version === this.previewReloadVersion) this.previewReloadError = true;
            } finally {
                if (version === this.previewReloadVersion) this.previewReloadPending = false;
            }
        },

        onQuoteAdded(event) {
            if (this.allowCreate && this.bookSlug) this.handleQuoteAdded(event.detail);
        },
        openQuoteModal() {
            if (!this.allowCreate || !this.bookSlug) return;
            // Вызываем метод модального Vue приложения
            if (window.modalApp && window.modalApp.showAddQuoteModal) {
                window.modalApp.showAddQuoteModal();
            }
        },
        handleQuoteAdded(newQuote) {
            if (!this.allowCreate || !this.bookSlug || !newQuote || newQuote.book_slug !== this.bookSlug
                || (newQuote.book?.slug && newQuote.book.slug !== this.bookSlug)) return;
            if (newQuote.is_public === false || newQuote.is_draft || (newQuote.status && newQuote.status !== 'active')) return;
            if (this.localQuotes.some(quote => quote.id === newQuote.id)) return;
            this.invalidatePreviewReload();
            this.localQuotes = [newQuote, ...this.localQuotes].slice(0, 4);
            this.localTotalCount += 1;
        },
        handleQuoteDeleted(quoteId) {
            if (!this.localQuotes.some(quote => quote.id === quoteId)) return;
            this.invalidatePreviewReload();
            this.localQuotes = this.localQuotes.filter(q => q.id !== quoteId);
            this.localTotalCount = Math.max(0, this.localTotalCount - 1);
            return this.refillPreviewIfNeeded();
        },
        handleQuoteUpdated(updatedQuote) {
            if (updatedQuote.is_public === false || updatedQuote.is_draft || (updatedQuote.status && updatedQuote.status !== 'active')) {
                this.handleQuoteDeleted(updatedQuote.id);
                return;
            }
            const index = this.localQuotes.findIndex(q => q.id === updatedQuote.id);
            if (index !== -1) {
                this.invalidatePreviewReload();
                this.$set(this.localQuotes, index, updatedQuote);
                return this.refillPreviewIfNeeded();
            }
        },
        handleLikeToggled(payload) {
            const quote = this.localQuotes.find(q => q.id === payload.quoteId);
            if (quote) {
                this.invalidatePreviewReload();
                this.$set(quote, 'is_liked_by_current_user', payload.isLiked);
                this.$set(quote, 'likes_count', payload.likesCount);
                return this.refillPreviewIfNeeded();
            }
        },
        showNotification(message, type = 'info') {
            this.$emit('show-notification', message, type);
        }
    }
};
</script>

<style scoped>
/* Styles inherited from parent */
</style>
